import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Avatar from '@material-ui/core/Avatar';
import { compose } from 'redux';
import { withRouter, Link } from 'react-router-dom';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import Mixpanel from 'utils/mixpanelService';
import Card from 'components/Card';
import withSizes from 'react-sizes';
import Hidden from '@material-ui/core/Hidden';
import Divider from 'components/Divider';
import { addHttps, getImageFile } from 'utils/stringUtils';
import { LazyLoadComponent } from 'react-lazy-load-image-component';

const styles = theme => ({
  cardRoot: {
    cursor: 'pointer',
    [theme.breakpoints.up('600')]: {
      height: '100%',
    },
  },
  labelWrapper: {
    height: 21,
    textAlign: 'left',
    [theme.breakpoints.down('600')]: {
      height: 35,
    },
  },
  label: {
    color: theme.palette.primary.main,
    [theme.breakpoints.down('600')]: {
      padding: '16px 16px 0 16px',
    },
  },
  listWrapper: {
    height: 100,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('600')]: {
      height: 50,
      display: 'flex',
      alignItems: 'flex-end',
    },
  },
  divider: {
    height: 1,
    width: 18,
    marginTop: 16,
    backgroundColor: '#E2E2E2',
    marginLeft: 8,
  },
  editorWrapper: {
    display: 'flex',
    alignItems: 'center',
    height: 32,
    marginTop: 16,
    [theme.breakpoints.down('600')]: {
      height: 50,
      marginTop: 0,
    },
  },
  list: {
    maxHeight: 73,
    width: '100%',
    overflow: 'hidden',
    [theme.breakpoints.down('600')]: {
      fontSize: 21,
      fontWeight: 450,
      padding: '0 16px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      display: '-webkit-box',
      margin: '0 auto',
      '-webkit-line-clamp': '2',
      '-webkit-box-orient': 'vertical',
      height: 50 /* Fallback for non-webkit */,
    },
  },
  listInfo: {
    width: '100%',
    marginTop: 6,
  },
  editor: {
    fontSize: 12,
    fontWeight: 600,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('600')]: {
      padding: '0 16px',
      color: '#3E4543',
    },
  },
  editorImage: {
    width: 40,
    height: 40,
    marginRight: 16,
  },
  editorLabel: {
    fontSize: 12,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  mobileImageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileEditorImage: {
    width: 99,
    height: 99,
  },
  link: {
    textDecoration: 'none',
  },
});

class EditorListItem extends React.PureComponent {
  redirect = item => {
    const { location } = this.props;
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track("Editor's List - Clicked", {
      name: item.fields.title,
      type: 'editors-list',
      page: finalPageName,
    });
  };

  render() {
    const { classes, item, isMobile, isSmall } = this.props;
    if (_isEmpty(item)) return null;

    let listName;
    if (isSmall) {
      listName =
        item.fields.title.length > 25 && item.fields.title.indexOf(' ', 24) > 0
          ? `${item.fields.title.substring(
              0,
              item.fields.title.indexOf(' ', 24),
            )}...`
          : item.fields.title;
    } else {
      listName =
        item.fields.title.length > 50
          ? `${item.fields.title.substring(0, 50)}...`
          : item.fields.title;
    }

    const hasEditor = !_isEmpty(item.fields.person);
    const editor = hasEditor && item.fields.person;
    const editorName = _get(editor, '0.fields.name', 'CredibleMind');
    const editorImage = getImageFile(_get(editor, '0.fields'));

    return (
      <Grid item xs={12} sm={4} md={3} key={item.sys.id}>
        <Card
          height={isMobile ? 'auto' : 225}
          // width={isMobile ? 'auto' : 260}
          background={isMobile ? 'transparent' : '#fff'}
          borderColor={isMobile ? 'none' : '#E9ECED'}
          padding={isMobile ? 0 : 16}
        >
          <Hidden xsDown>
            <Link
              to={`/lists/${item.fields.slug}`}
              onClick={() => this.redirect(item)}
              className={classes.link}
            >
              <div role="button" className={classes.cardRoot} tabIndex={0}>
                <div className={classes.labelWrapper}>
                  <Typography
                    align="left"
                    className={classes.label}
                    variant="overline"
                  >
                    LIST
                  </Typography>
                </div>
                <div className={classes.listWrapper}>
                  <Typography
                    align="left"
                    className={classes.list}
                    color="textPrimary"
                    variant="h6"
                  >
                    {listName}
                  </Typography>
                  <Typography
                    align="left"
                    className={classes.listInfo}
                    color="textPrimary"
                    variant="overline"
                  >
                    {item.fields.resources.length} Resources
                  </Typography>
                </div>
                <div className={classes.divider} />
                <div className={classes.editorWrapper}>
                  {editorImage && (
                    <LazyLoadComponent>
                      <Avatar
                        alt={editorName}
                        src={addHttps(editorImage)}
                        className={classes.editorImage}
                      />
                    </LazyLoadComponent>
                  )}
                  <div>
                    <Typography
                      align="left"
                      className={classes.editorLabel}
                      color="textPrimary"
                    >
                      List Created By
                    </Typography>
                    <Typography align="left" className={classes.editor}>
                      {editorName}
                    </Typography>
                  </div>
                </div>
              </div>
            </Link>
          </Hidden>
          <Hidden smUp>
            <Grid container>
              <Grid item xs={12}>
                <Divider bottom={10} top={10} />
              </Grid>
              <Link
                to={`/lists/${item.fields.slug}`}
                onClick={() => this.redirect(item)}
                className={classes.link}
              >
                <Grid
                  container
                  item
                  xs={12}
                  direction="row"
                  className={classes.cardRoot}
                >
                  <Grid item xs={4} className={classes.mobileImageWrapper}>
                    {editorImage && (
                      <LazyLoadComponent>
                        <Avatar
                          alt={editorName}
                          src={editorImage}
                          className={classes.mobileEditorImage}
                        />
                      </LazyLoadComponent>
                    )}
                  </Grid>
                  <Grid item xs={8} container>
                    <Grid item xs={12} className={classes.labelWrapper}>
                      <Typography
                        align="left"
                        className={classes.label}
                        variant="overline"
                      >
                        LIST
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className={classes.listWrapper}>
                      <Typography
                        align="left"
                        className={classes.list}
                        color="textPrimary"
                        variant="h6"
                      >
                        {item.fields.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} className={classes.editorWrapper}>
                      <Typography align="left" className={classes.editor}>
                        {`${editorName}`}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          </Hidden>
        </Card>
      </Grid>
    );
  }
}

EditorListItem.propTypes = {
  classes: PropTypes.object,
  item: PropTypes.object,
  location: PropTypes.object,
  isMobile: PropTypes.bool,
  isSmall: PropTypes.bool,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 600,
  isSmall: width < 800,
});

export default compose(
  withStyles(styles),
  withSizes(mapSizesToProps),
  withRouter,
)(EditorListItem);
